import { canOpenPortalJob } from '../../utils/portalJobNavigation';
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMsal } from '@azure/msal-react';
import { useNavigate } from "react-router-dom";
import { Accordion, Table, Spinner, Alert, Form, Col, Button, Nav } from "react-bootstrap";
import { checkoutJob, clearJob } from "../../redux/slices/checkoutSlice";
import { setJobId, clearJobId } from "../../redux/slices/jobIdSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectUserDetails, selectUserRoles } from "../../redux/slices/userSlice";
import { selectCustomRoles } from "../../redux/slices/customRolesSlice";
import { mapUserRoles } from "../../functions/useRoleMapping";
import { resetApiUrls } from "../../redux/slices/apiUrlSlice";
import { clearJobDetails } from "../../redux/slices/jobDetailsSlice";
import { clearPumpDetails } from "../../redux/slices/pumpDetailsSlice";
import useCriticalData from "../../functions/useCriticalData";
import { clearZones, setZones } from "../../redux/slices/zoneCountSlice";
import CreateJobModal from "./CreateJobModal";
import { clearHasValues } from "../../redux/slices/hasValuesSlice";
import { clearApiStatus, updateApiStatus } from "../../redux/slices/apiStatusSlice";
import "../../App.css"; // Import the global CSS file
import JobActionModal from "./JobActionModal";
import { clearActiveTab } from "../../redux/slices/activeMechanicalTabSlice";
import { setReadOnly } from "../../redux/slices/readOnlySlice";
import { fetchData } from "../../redux/slices/apiCallSlice";
import { fetchBackgroundData } from '../../redux/api/portalApi';
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import { getProjectStatus } from "../../utils/dataNormalization";
import { usePermissions } from "../../hooks/usePermissions";
import { useRealtimeResource } from '../../realtime/RealtimeProvider';

const isTruthyCheckedOut = (value) =>
  value === true ||
  value === 1 ||
  String(value || "").trim().toLowerCase() === "true" ||
  String(value || "").trim().toLowerCase() === "yes";

const normalizeUserName = (value) => String(value || "").trim().toLowerCase();
const COMPACT_JOB_LIST_QUERY =
  "(max-width: 767.98px), (min-width: 768px) and (max-width: 1366px) and (hover: none) and (pointer: coarse)";

const getIsCompactJobList = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(COMPACT_JOB_LIST_QUERY).matches;

function JobList() {
  const { instance, accounts } = useMsal();
  const userDetails = useSelector(selectUserDetails); // User details
  const currentUser = userDetails?.name;
  const userRoles = useSelector(selectUserRoles); // User details
  const customRoles = useSelector(selectCustomRoles);
  const { canEdit } = usePermissions();
  const [roles, setRoles] = useState(
    mapUserRoles(userRoles || [], userDetails?.username, {
      customRoles: { roles: customRoles || {} },
    })
  );

  useEffect(() => {
      setRoles(
        mapUserRoles(userRoles || [], userDetails?.username, {
          customRoles: { roles: customRoles || {} },
        })
      );
    }, [userRoles, userDetails?.username, customRoles]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Load critical data including dropdownValues, globalVariables, hoverDescriptions, and user dropdowns
  // Force refresh to ensure we fetch after cache clear in JobListContainer
  useCriticalData({
    apiKeys: ['dropdownValues', 'globalVariables', 'hoverDescriptions'],
    includeUserDropdowns: true,
    autoLoad: true,
    forceRefresh: false
  });

  useEffect(() => {
    dispatch(resetApiUrls()); // Reset API URLs when the component mounts
    dispatch(clearJob()); // Clear job when the component mounts
    dispatch(clearZones()); // Clear zones when the component mounts
    dispatch(clearActiveTab()); // Clear active mechanical tab when the component mounts
    dispatch(clearJobId()); // Clear job ID when the component mounts
    dispatch(clearApiStatus()); // Clear API status when the component mounts
    dispatch(clearJobDetails()); // Clear job details when the component mounts
    dispatch(clearPumpDetails()); // Clear pump details when the component mounts
    dispatch(clearHasValues()); // Clear hasValues when the component mounts
    dispatch(clearApiStatus()); // Clear API status when the component mounts
    // Note: Don't clear dropdowns - they're managed by useCriticalData with caching
    // Note: globalVariables and hoverDescriptions are now loaded via useCriticalData hook
    // Note: userDropdowns are now loaded via useCriticalData hook
  }, [dispatch, instance, accounts]);

  const apiUrls = useSelector(selectApiUrls);

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("active");
  const [isCompactJobList, setIsCompactJobList] = useState(getIsCompactJobList);
  const hasLoadedJobs = jobs.length > 0;
  const showInitialLoading = loading && !hasLoadedJobs;

  const [filters, setFilters] = useState({
    modelNumber: "", // "VT", "HC", "CP" filter or All
    salesRep: "",
    projectStatus: "",
    drawingStatus: "",
    projectManager: "",
    justMyJobs: roles.Apps || roles.Sales || roles.MechanicalEngineer || roles.ElectricalEngineer, // Default based on roles
    hasProjectId: true,
  });

  const handleIncludeShippedJobs = () => {
    setActiveTab("shipped");
  };

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;

    const compactQuery = window.matchMedia(COMPACT_JOB_LIST_QUERY);
    const handleCompactChange = (event) => setIsCompactJobList(event.matches);

    setIsCompactJobList(compactQuery.matches);
    compactQuery.addEventListener?.("change", handleCompactChange);

    return () => {
      compactQuery.removeEventListener?.("change", handleCompactChange);
    };
  }, []);

  const [showModal, setShowModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const canCreateJob = canEdit('portal.job-list.create-job');
  const viewIdentity = `${accounts[0]?.homeAccountId || ''}:${apiUrls.generalProjectInfoEnhanced}:${apiUrls.shippedProjects}:${activeTab}`;
  const currentView = useRef(viewIdentity);
  currentView.current = viewIdentity;
  const mounted = useRef(false);
  const foregroundGeneration = useRef(0);
  const backgroundBlocked = useRef(false);
  backgroundBlocked.current = loading || showModal || showActionModal;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // API call to fetch all jobs (no pagination)
  const fetchJobs = useCallback(async (targetTab = activeTab, { background = false, signal } = {}) => {
    if (background && (signal?.aborted || backgroundBlocked.current)) return false;
    if (!background) {
      foregroundGeneration.current++;
      setLoading(true);
      setError(null); // Clear errors only for ordinary foreground loads.
    }
    const generation = foregroundGeneration.current;
    const requestedView = viewIdentity;
    const canApplyBackground = () => !signal?.aborted && mounted.current && currentView.current === requestedView
      && foregroundGeneration.current === generation && !backgroundBlocked.current;
    let request;
    try {
      if (!background) dispatch(updateApiStatus({ tableName: 'generalProject', status: 'pending' }));

      const args = targetTab === "shipped"
        ? { url: apiUrls.shippedProjects }
        : {
            url: `${apiUrls.generalProjectInfoEnhanced}?${new URLSearchParams({
              hideFromListNotEqual: 'true',
              page: '1',
              projectStatusNotEqual: 'Shipped',
              scheduleOnlyNotEqual: 'Yes',
            }).toString()}`,
          };
      request = background ? dispatch(fetchBackgroundData({ ...args, signal, contract: {} })) : dispatch(fetchData(args));
      const data = await (background ? request : request.unwrap());
      if (background && (!canApplyBackground() || !Array.isArray(data))) return false;

      if (data.length > 0) {
        const normalizedJobs = data.map((job) => ({
          ...job,
          ID: job.ID ?? job.id,
          JobNumber: job.JobNumber ?? job.jobNumber,
          ProjectName: job.ProjectName ?? job.projectName,
          ModelNumber: job.ModelNumber ?? job.modelNumber,
          SalesRep: job.SalesRep ?? job.salesRep,
          ProjectStatus: job.ProjectStatus ?? job.projectStatus,
          DrawingStatus: job.DrawingStatus ?? job.drawingStatus,
          ProjectManager: job.ProjectManager ?? job.projectManager,
          ActualShipDate: job.ActualShipDate ?? job.actualShipDate,
          MechanicalEngineer: job.MechanicalEngineer ?? job.mechanicalEngineer,
          ElectricalEngineer: job.ElectricalEngineer ?? job.electricalEngineer,
          ControlsEngineer: job.ControlsEngineer ?? job.controlsEngineer,
          IsCheckedOut: job.IsCheckedOut ?? job.isCheckedOut,
          CheckedOutBy: job.CheckedOutBy ?? job.checkedOutBy,
          HideFromList: job.HideFromList ?? job.hideFromList,
          ScheduleOnly: job.ScheduleOnly ?? job.scheduleOnly,
          HasIncompleteMechanical: job.HasIncompleteMechanical ?? job.hasIncompleteMechanical ?? false,
          HasIncompleteElectrical: job.HasIncompleteElectrical ?? job.hasIncompleteElectrical ?? false,
          HasIncompleteControls: job.HasIncompleteControls ?? job.hasIncompleteControls ?? false,
          TBDs: Array.isArray(job.TBDs)
            ? job.TBDs
            : Array.isArray(job.tbds)
            ? job.tbds
            : [],
        }));

        // Sort jobs by Job Number in descending order, treating Job Number as a number
        const sortedData = [...normalizedJobs].sort((a, b) => {
          const parseJobNumber = (jobNumber) => {
            const [base, suffix] = jobNumber.split('-').map(Number);
            return { base: base || 0, suffix: suffix || 0 };
          };

          const aParsed = parseJobNumber(a.JobNumber || "");
          const bParsed = parseJobNumber(b.JobNumber || "");

          if (aParsed.base !== bParsed.base) {
            return bParsed.base - aParsed.base; // Compare base numbers
          }
          return bParsed.suffix - aParsed.suffix; // Compare suffix numbers
        });
        setJobs(sortedData); // Set all jobs
        if (!background) {
          setFilteredJobs(sortedData); // Foreground behavior is preserved.
          dispatch(updateApiStatus({ tableName: 'generalProject', status: 'success' }));
        }
      } else {
        if (background) setJobs([]); // Reflect a real empty result after deletions/status changes.
        else {
          setError("No jobs found");
          dispatch(updateApiStatus({ tableName: 'generalProject', status: 'no data' }));
        }
      }
      return true;
    } catch (err) {
      if (!background) {
        console.error("Error fetching jobs:", err);
        dispatch(updateApiStatus({ tableName: 'generalProject', status: 'failed', error: err?.status || err?.message }));
        setError("Failed to fetch jobs");
      }
      return false; // Failed background refresh retains the existing rows and retries separately.
    } finally {
      if (background) request?.unsubscribe?.();
      else setLoading(false);
    }
  }, [dispatch, apiUrls.generalProjectInfoEnhanced, apiUrls.shippedProjects, activeTab, viewIdentity]);

  useRealtimeResource({
    enabled: true,
    ownerKey: `job-list:${viewIdentity}`,
    resources: [activeTab === 'shipped' ? 'portal:shipped-projects' : 'portal:general-project-info-enhanced',
      'portal:general-project-info', 'portal:assembly-schedule', 'portal:shipping',
      'portal:soldto-shipto-info', 'portal:price-breakdown-info'],
    blocked: loading || showModal || showActionModal,
    refresh: ({ signal }) => fetchJobs(activeTab, { background: true, signal }),
  });

  // Fetch jobs when component mounts
  useEffect(() => {
    fetchJobs(activeTab);
  }, [fetchJobs, activeTab]);

  const hasTbd = (job) => Array.isArray(job?.TBDs) && job.TBDs.length > 0;

  const hasOpenChangeOrderTasks = (job) => (
    Boolean(job?.HasIncompleteMechanical) ||
    Boolean(job?.HasIncompleteElectrical) ||
    Boolean(job?.HasIncompleteControls)
  );

  const handleRowClick = (jobId) => {
    const job = jobs.find((job) => job.ID === jobId);
    if (!canOpenPortalJob(job)) return;

    // Checked-out jobs skip modal:
    // - owner opens editable
    // - non-owner opens read-only
    if (isTruthyCheckedOut(job?.IsCheckedOut)) {
      const isCheckedOutByCurrentUser =
        normalizeUserName(job?.CheckedOutBy) === normalizeUserName(currentUser);

      if (isCheckedOutByCurrentUser) {
        handleJobAction(jobId, false, true);
      } else {
        handleJobAction(jobId, false);
      }
      return;
    }

    const allowedRolesForModal = [
      roles.Admin,
      roles.Apps,
      roles.MechanicalEngineer,
      roles.ElectricalEngineer,
      roles.ControlEngineer,
      roles.PSN,
    ];

    if (allowedRolesForModal.some((role) => role)) {
      setSelectedJobId(jobId);
      setShowActionModal(true); // Show the modal for allowed roles
    } else {
      handleJobAction(jobId, false); // Open read-only for users without edit permissions
    }
  };

  const handleJobAction = async (jobId, shouldCheckout, forceEditableWithoutCheckout = false) => {
    dispatch(setJobId(jobId));
    // Note: fetchJobDetails removed - useJobHandler will handle fetching via fetchFullJob
    // This prevents race conditions where fetchJobDetails sets 'pending' status
    // that conflicts with fullJobFetch's status management
    
    // Always set the zone to 1 when a job is selected
    dispatch(setZones(1));

    if (shouldCheckout) {
      dispatch(setReadOnly(false));
      try {
        const resultAction = await dispatch(checkoutJob({ jobId, currentUser }));

        if (checkoutJob.rejected.match(resultAction)) {
          console.warn("Failed to checkout job:", resultAction.payload || resultAction.error);
          dispatch(setReadOnly(true));
          navigate(`/${jobId}/generalInfo`);
          return;
        }
      } catch (error) {
        console.error("Failed to check out the job:", error);
        dispatch(setReadOnly(true));
        navigate(`/${jobId}/generalInfo`);
        return;
      }
    } else {
      dispatch(setReadOnly(!forceEditableWithoutCheckout));
    }

    navigate(`/${jobId}/generalInfo`);
  };

  // Handle Filter Change
  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Filter Jobs based on selected filters

  useEffect(() => {
    if (!jobs || jobs.length === 0) {
      setFilteredJobs([]);
      return;
    }

    const filtered = jobs.filter((job) => {
      const projectStatus = getProjectStatus(job);

      // Exclude jobs where HideFromList is true
      if (job.HideFromList === true) return false;
      
      // Exclude jobs where ScheduleOnly is Yes
      if (job.ScheduleOnly === "Yes") return false;

      const modelFilter = filters.modelNumber
        ? filters.modelNumber === "other"
          ? !["VT", "HC", "CP", "WM", "SH"].some((prefix) =>
              job.ModelNumber?.startsWith(prefix)
            )
          : job.ModelNumber?.startsWith(filters.modelNumber)
        : true;

      const salesRepFilter = filters.salesRep
        ? job.SalesRep === filters.salesRep
        : true;

      const projectStatusFilter = filters.projectStatus
        ? projectStatus === filters.projectStatus
        : true;

      const drawingStatusFilter = filters.drawingStatus
        ? job.DrawingStatus === filters.drawingStatus
        : true;

      const projectManagerFilter = filters.projectManager
        ? job.ProjectManager === filters.projectManager
        : true;

      const justMyJobsFilter = filters.justMyJobs
        ? [job.SalesRep, job.ProjectManager, job.MechanicalEngineer, job.ElectricalEngineer, job.ControlsEngineer].includes(currentUser)
        : true;

      const searchFilter = searchTerm
        ? job.JobNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.ProjectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.ModelNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.SalesRep?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          projectStatus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.DrawingStatus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.ProjectManager?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.MechanicalEngineer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.ElectricalEngineer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.ControlsEngineer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.ID?.toString().includes(searchTerm)
        : true;

      return (
        modelFilter &&
        salesRepFilter &&
        projectStatusFilter &&
        drawingStatusFilter &&
        projectManagerFilter &&
        justMyJobsFilter &&
        searchFilter
      );
    });

    setFilteredJobs(filtered);
  }, [searchTerm, filters, jobs, currentUser]);

  // Get unique values for filters
  const getUniqueValues = (key) => [
    ...new Set(jobs.map((job) => job[key]).filter((val) => val)),
  ];

  const getJobRowClass = (job) => {
    const projectStatus = getProjectStatus(job);
    return projectStatus === "canceled"
      ? "row-canceled"
      : projectStatus === "On Hold"
      ? "row-on-hold"
      : "";
  };

  const mobileFilterKeys = [
    "modelNumber",
    "salesRep",
    "projectStatus",
    "drawingStatus",
    "projectManager",
  ];
  const activeMobileFilterCount = mobileFilterKeys.filter((key) => filters[key]).length;

  const clearMobileFilters = () => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      modelNumber: "",
      salesRep: "",
      projectStatus: "",
      drawingStatus: "",
      projectManager: "",
    }));
  };

  return (
    
    <div className="job-list-page m-4">
      {/* Error message */}
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="job-list-title mb-3 text-center"><h1>Production Portal</h1></div>
      
      <CreateJobModal show={showModal} onHide={() => setShowModal(false)} />
      <JobActionModal
        show={showActionModal}
        onHide={() => setShowActionModal(false)}
        onEdit={handleJobAction}
        jobId={selectedJobId}
      />
      <Nav
        variant="tabs"
        activeKey={activeTab}
        onSelect={(eventKey) => {
          if (!eventKey || eventKey === activeTab) return;
          setActiveTab(eventKey);
        }}
        className="job-list-tabs mb-3"
      >
        <Nav.Item>
          <Nav.Link eventKey="active">Active Jobs</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="shipped">Shipped Jobs</Nav.Link>
        </Nav.Item>
      </Nav>

      <div className="job-list-toolbar d-flex justify-content-between mb-3">
        <Form.Group as={Col} md="4" className="job-list-search">
          <Form.Label>Search Jobs</Form.Label>
          <Form.Control
            type="text"
            placeholder="Search by Job #, Project Name, Model #, Sales Rep, Status, Project Manager, Engineers, or ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} // Update the search term
          />
        </Form.Group>
        <Form.Group as={Col} md="2" className="job-list-my-jobs d-flex align-items-end">
          <Col>          <Form.Check
            type="checkbox"
            label="Filter My Jobs"
            name="justMyJobs"
            checked={filters.justMyJobs}
            onChange={handleFilterChange} // Update the "Just My Jobs" filter
          /></Col>
        </Form.Group>
        {canCreateJob ? (
          <Button className="job-list-create-button" onClick={() => setShowModal(true)}>Create New Job</Button>
        ) : null}
      </div>

      {isCompactJobList && (
        <Accordion className="job-list-mobile-filters mb-3">
        <Accordion.Item eventKey="filters">
          <Accordion.Header>
            Filters
            {activeMobileFilterCount > 0 && (
              <span className="job-list-mobile-filter-count ms-2">
                {activeMobileFilterCount}
              </span>
            )}
          </Accordion.Header>
          <Accordion.Body>
            <div className="job-list-mobile-filter-grid">
              <Form.Group>
                <Form.Label>Model Number</Form.Label>
                <Form.Control
                  as="select"
                  name="modelNumber"
                  value={filters.modelNumber}
                  onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  <option value="CP">CP</option>
                  <option value="HC">HC</option>
                  <option value="SH">SH</option>
                  <option value="VT">VT</option>
                  <option value="WM">WM</option>
                  <option value="other">Other</option>
                </Form.Control>
              </Form.Group>

              <Form.Group>
                <Form.Label>Sales Rep</Form.Label>
                <Form.Control
                  as="select"
                  name="salesRep"
                  value={filters.salesRep}
                  onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  {getUniqueValues("SalesRep").map((rep) => (
                    <option key={rep} value={rep}>{rep}</option>
                  ))}
                </Form.Control>
              </Form.Group>

              <Form.Group>
                <Form.Label>Project Status</Form.Label>
                <Form.Control
                  as="select"
                  name="projectStatus"
                  value={filters.projectStatus}
                  onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  {getUniqueValues("ProjectStatus").map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </Form.Control>
              </Form.Group>

              <Form.Group>
                <Form.Label>Drawing Status</Form.Label>
                <Form.Control
                  as="select"
                  name="drawingStatus"
                  value={filters.drawingStatus}
                  onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  {getUniqueValues("DrawingStatus").map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </Form.Control>
              </Form.Group>

              <Form.Group>
                <Form.Label>Project Manager</Form.Label>
                <Form.Control
                  as="select"
                  name="projectManager"
                  value={filters.projectManager}
                  onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  {getUniqueValues("ProjectManager").map((manager) => (
                    <option key={manager} value={manager}>{manager}</option>
                  ))}
                </Form.Control>
              </Form.Group>
            </div>

            {activeMobileFilterCount > 0 && (
              <Button
                type="button"
                variant="outline-secondary"
                className="job-list-mobile-clear-filters mt-3"
                onClick={clearMobileFilters}
              >
                Clear Filters
              </Button>
            )}
          </Accordion.Body>
        </Accordion.Item>
        </Accordion>
      )}

      {filteredJobs.length === 0 && !loading && jobs.length > 0 && (
        <Alert variant="info" className="job-list-empty-alert d-flex align-items-center justify-content-between">
          <span>
            {activeTab === "active"
              ? "No results found. Search shipped jobs instead?"
              : "No results found in shipped jobs."}
          </span>
          {activeTab === "active" && (
            <Button variant="primary" size="sm" onClick={handleIncludeShippedJobs}>
              Search shipped jobs
            </Button>
          )}
        </Alert>
      )}

      {/* Job table */}
      {!isCompactJobList && (
        <div className="job-list-table-shell" role="region" aria-label="Jobs table" tabIndex={0}>
        <Table striped bordered hover className="job-list-table">
        <thead>
          <tr>
            <th>
              <i className="bi bi-person-fill-lock"></i>
            </th>
            <th>Job Number</th>
            <th>Project Name</th>
            <th>
              {" "}
              <Form.Label>Model Number</Form.Label>
              <Form.Control
                as="select"
                name="modelNumber"
                value={filters.modelNumber}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                <option value="CP">CP</option>
                <option value="HC">HC</option>
                <option value="SH">SH</option>
                <option value="VT">VT</option>
                <option value="WM">WM</option>
                <option value="other">Other</option>
              </Form.Control>
            </th>
            <th>
              {" "}
              <Form.Label>Sales Rep</Form.Label>
              <Form.Control
                as="select"
                name="salesRep"
                value={filters.salesRep}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                {getUniqueValues("SalesRep").map((rep) => (
                  <option key={rep} value={rep}>
                    {rep}
                  </option>
                ))}
              </Form.Control>
            </th>
            <th>
              {" "}
              <Form.Label>Project Status</Form.Label>
              <Form.Control
                as="select"
                name="projectStatus"
                value={filters.projectStatus}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                {getUniqueValues("ProjectStatus").map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Form.Control>
            </th>
            <th>
              {" "}
              <Form.Label>Drawing Status</Form.Label>
              <Form.Control
                as="select"
                name="drawingStatus"
                value={filters.drawingStatus}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                {getUniqueValues("DrawingStatus").map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Form.Control>
            </th>
            <th>
              <Form.Label>Project Manager</Form.Label>
              <Form.Control
                as="select"
                name="projectManager"
                value={filters.projectManager}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                {getUniqueValues("ProjectManager").map((manager) => (
                  <option key={manager} value={manager}>
                    {manager}
                  </option>
                ))}
              </Form.Control>
            </th>
            {activeTab === "shipped" && <th>Ship Date</th>}
          </tr>
        </thead>
        <tbody>
          {filteredJobs.map((job) => (
            <tr
              key={job.ID}
              onClick={canOpenPortalJob(job) ? () => handleRowClick(job.ID, job.JobNumber) : undefined}
              className={getJobRowClass(job)}
            >
              <td>
                {job.IsCheckedOut && (
                  <i
                    className="bi bi-person-fill-lock"
                    title={`Checked out by: ${job.CheckedOutBy}`}
                  ></i>
                )}
              </td>
              <td>
                {job.JobNumber}
                {hasTbd(job) && (
                  <i
                    className="bi bi-exclamation-lg"
                    style={{ color: "red" }}
                    title={`This job has ${job.TBDs.length} TBD${job.TBDs.length === 1 ? "" : "s"}`}
                  ></i>
                )}
                {hasOpenChangeOrderTasks(job) && (
                  <i
                    className="bi bi-person-lines-fill ms-1"
                    style={{ color: "orange" }}
                    title="This job has incomplete change order tasks"
                  ></i>
                )}
              </td>
              <td>{job.ProjectName}</td>
              <td>{job.ModelNumber}</td>
              <td>{job.SalesRep}</td>
              <td>{getProjectStatus(job)}</td>
              <td>{job.DrawingStatus}</td>
              <td>{job.ProjectManager}</td>
              {activeTab === "shipped" && (
                <td>{job.ActualShipDate ? String(job.ActualShipDate).split("T")[0] : ""}</td>
              )}
            </tr>
          ))}
        </tbody>
        </Table>
        </div>
      )}

      {isCompactJobList && (
        <div className="job-list-mobile-cards" aria-label="Jobs">
        <div className="job-list-tablet-column-headings" aria-hidden="true">
          <span>Job Number</span>
          <span>Project</span>
          <span>Status</span>
        </div>
        {filteredJobs.map((job) => (
          <button
            key={job.ID}
            type="button"
            className={`job-list-mobile-card ${getJobRowClass(job)}`}
            onClick={canOpenPortalJob(job) ? () => handleRowClick(job.ID, job.JobNumber) : undefined}
            aria-label={`Open job ${job.JobNumber}: ${job.ProjectName}`}
          >
            <span className="job-list-mobile-card-header">
              <span className="job-list-mobile-job-number">
                {job.JobNumber}
                {hasTbd(job) && (
                  <i
                    className="bi bi-exclamation-lg ms-1 text-danger"
                    title={`This job has ${job.TBDs.length} TBD${job.TBDs.length === 1 ? "" : "s"}`}
                  ></i>
                )}
                {hasOpenChangeOrderTasks(job) && (
                  <i
                    className="bi bi-person-lines-fill ms-1 text-warning"
                    title="This job has incomplete change order tasks"
                  ></i>
                )}
              </span>
              <span className="job-list-mobile-status">
                {getProjectStatus(job) || "No status"}
              </span>
            </span>

            <span className="job-list-mobile-project-name">
              {job.ProjectName || "Unnamed project"}
            </span>

            <span className="job-list-mobile-details">
              <span className="job-list-mobile-detail job-list-mobile-detail-inline job-list-mobile-model">
                <span>{job.ModelNumber || "—"}</span>
              </span>
              <span className="job-list-mobile-detail job-list-mobile-sales-rep">
                <span className="job-list-mobile-card-label">Sales Rep</span>
                <span>{job.SalesRep || "—"}</span>
              </span>
              <span className="job-list-mobile-detail job-list-mobile-project-manager">
                <span className="job-list-mobile-card-label">Project Manager</span>
                <span>{job.ProjectManager || "—"}</span>
              </span>
              {activeTab === "shipped" && (
                <span className="job-list-mobile-detail job-list-mobile-ship-date">
                  <span className="job-list-mobile-card-label">Ship Date</span>
                  <span>
                    {job.ActualShipDate ? String(job.ActualShipDate).split("T")[0] : "—"}
                  </span>
                </span>
              )}
            </span>

            {isTruthyCheckedOut(job.IsCheckedOut) && (
              <span className="job-list-mobile-checkout">
                <i className="bi bi-person-fill-lock me-2"></i>
                Checked out{job.CheckedOutBy ? ` by ${job.CheckedOutBy}` : ""}
              </span>
            )}
          </button>
        ))}
        </div>
      )}

      {/* Loading spinner */}
      {showInitialLoading && <Spinner animation="border" />}
    </div>
  );
}

export default JobList;
